import { useContext } from "react";
import { ModalContext } from "contexts/Modal";
import { logError, logInfo } from "util/logging";
import { strNonEmpty } from "util/strings";
import { API, Auth } from "aws-amplify";

const ensureAuthed = async () => {
  // Gate all API calls on a valid Amplify session
  await Auth.currentAuthenticatedUser();
};

const useEditAccount = (brandId) => {
  const { showModal, showConfirm } = useContext(ModalContext);

  const handleSaveAccount = async (
    event,
    brandId,
    initInputs,
    inputs,
    setModalOpen,
    setListAccounts,
    accounts,
    accountId = "",
    updateBrandAccounts
  ) => {
    if (event) event.preventDefault();

    try {
      // Validate UI inputs first
      if (!validateInputs(accountId, inputs, showModal)) return;

      // Ensure we’re authenticated before any API work
      await ensureAuthed();

      if (!strNonEmpty(accountId)) {
        await createAccount(
          brandId,
          inputs,
          showModal,
          setListAccounts,
          updateBrandAccounts
        );
      } else {
        await updateAccount(
          accountId,
          initInputs,
          inputs,
          showModal,
          setListAccounts,
          accounts,
          brandId,
          updateBrandAccounts
        );
      }

      setModalOpen(false);
    } catch (err) {
      logError("useEditAccount.js: Error occurred while updating account (2)", err);
      showModal("Save Account", "Error occurred while saving account. (7)");
    }
  };

  const handleDeactivateAccount = async (brandId, accountId, updateBrandAccounts) => {
    try {
      showConfirm(
        "Deactivate Account",
        "Do you really want to deactivate the account?",
        async () => {
          try {
            await ensureAuthed();
            await deactivateAccount(brandId, accountId, showModal, updateBrandAccounts);
          } catch (err) {
            logError("useEditAccount.js handleDeactivateAccount auth/API error", err);
            showModal(
              "Deactivate Account",
              "An error occurred while deactivating account."
            );
          }
        }
      );
    } catch (err) {
      logError("useEditAccount.js handleDeactivateAccount error: Unknown error", err);
    }
  };

  return {
    handleSaveAccount,
    handleDeactivateAccount,
  };
};

const validateInputs = (accountId, inputs, showModal) => {
  const accountNameNonEmpty = strNonEmpty(inputs.accountName);

  if (strNonEmpty(accountId)) {
    if (accountNameNonEmpty) return true;
    showModal("Edit Account Name", "Account name can't be empty");
    return false;
  } else {
    if (accountNameNonEmpty) return true;
    showModal("Create Account", "Account name can't be empty");
    return false;
  }
};

const createAccount = async (
  brandId,
  inputs,
  showModal,
  setListAccounts,
  updateBrandAccounts
) => {
  try {
    await ensureAuthed();

    const accountToCreate = {
      name: inputs.accountName,
      brand: brandId,
    };

    await API.post("AccountGatewayApi", `/account`, {
      body: accountToCreate
    });

    updateBrandAccounts(true);
  } catch (err) {
    logError("createAccount error", err);
    showModal("Add Account", "An unknown error occurred. (3)");
  }
};

const updateAccount = async (
  accountId,
  initInputs,
  inputs,
  showModal,
  setListAccounts,
  accounts,
  brandId,
  updateBrandAccounts
) => {
  try {
    await ensureAuthed();

    if (initInputs.accountName === inputs.accountName) {
      return true; // nothing to update
    }

    // remove falsy keys (existing behavior)
    Object.keys(inputs).forEach((key) => {
      if (!inputs[key]) delete inputs[key];
    });

    await API.patch("AccountGatewayApi", `/account/${accountId}`, {
      body: { name: inputs.accountName }
    });

    updateBrandAccounts(true);
  } catch (err) {
    logError("useEditAccount.js: Error occurred while updating account (3)", err);
    showModal("Save Account", "Error occurred while saving account. (6)");
    return false;
  }
};

const deactivateAccount = async (brandId, accountId, showModal, updateBrandAccounts) => {
  try {
    await ensureAuthed();

    const removedAccount = await API.patch("AccountGatewayApi", `/account/${accountId}`, {
      body: { brand: "default" }
    });

    logInfo("useProfile adminDisableAccount successful", removedAccount);
    updateBrandAccounts(true);
  } catch (err) {
    logError("useEditAccount.js deactivateAccount Error", err);
    showModal("Deactivate Account", "An error occurred while deactivating account.");
  }
};

export default useEditAccount;
